import { forwardRef, FC, ReactNode, Ref, PropsWithoutRef } from 'react';
import styled from 'styled-components';

import { useUID } from '../../hooks';
import { BaseProps, ForwardProps } from '../../types';
import Flex from '../Flex';
import Text from '../Text';

export interface FormProps extends BaseProps {
  /** The Form content. Recommended composing form elements within a layout template such as Grid or Flex as the only child. */
  children: ReactNode;
  /** Region for Form Actions. */
  actions?: ReactNode;
  /** The heading of the form. */
  heading?: string;
  /** A description of the form to be displayed above the controls. */
  description?: string;
  /** Region for Banners above the Form. */
  banners?: ReactNode;
  /** Ref forwarded to the wrapping element. */
  ref?: Ref<HTMLFormElement>;
}

export const StyledForm = styled.form``;
export const StyledFormContent = styled.div``;

const Form: FC<FormProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<FormProps>, ref: FormProps['ref']) => {
    const { children, actions, heading, description, banners, ...restProps } = props;

    const uid = useUID();

    return (
      <Flex
        container={{ direction: 'column', gap: 3 }}
        as={StyledForm}
        ref={ref}
        {...restProps}
        aria-labelledby={heading ? `${uid}-heading` : undefined}
        aria-describedby={description ? `${uid}-description` : undefined}
      >
        {banners}
        {(heading || description) && (
          <Flex container={{ direction: 'column', gap: 1 }}>
            {heading && (
              <Text id={`${uid}-heading`} variant='h3'>
                {heading}
              </Text>
            )}
            {description && (
              <Text id={`${uid}-description`} as='p'>
                {description}
              </Text>
            )}
          </Flex>
        )}
        <Flex as={StyledFormContent} container={{ direction: 'column', gap: 3 }}>
          {children}
        </Flex>

        {actions && <Flex container={{ justify: 'between', alignItems: 'center' }}>{actions}</Flex>}
      </Flex>
    );
  }
);

export default Form;
